import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const dict = {
  en: {
    home: 'Home', destinations: 'Destinations', packages: 'Packages', flights: 'Flights', hotels: 'Hotels', contact: 'Contact',
    login: 'Login', logout: 'Logout', myBookings: 'My Bookings', myProfile: 'My Profile', adminPanel: 'Admin Panel',
    heroTitle: 'Discover Your Next Adventure', heroSub: 'From the Himalayas to the Maldives — handcrafted journeys by TripSoul Travels, Kuwait',
    exploreDestinations: 'Explore Destinations', viewPackages: 'View Packages',
    popularDestinations: 'Popular Destinations', popularSub: 'Explore our most loved destinations across India and the world',
    featuredPackages: 'Featured Packages', featuredSub: 'Carefully curated travel experiences for unforgettable memories',
    ourServices: 'Our Services', servicesSub: 'Everything you need for a perfect trip',
    testimonials: 'What Our Travelers Say', testimonialsSub: 'Real experiences from our satisfied customers',
    writeReview: 'Write a Review', loginToReview: 'Login to write a review',
    bookNow: 'Book Now', viewMap: 'View Map', photo: 'Photo', viewItinerary: 'View Itinerary', bookPackage: 'Book This Package',
    perPerson: 'per person', search: 'Search', reserve: 'Reserve', book: 'Book',
    payNow: 'Pay now', paid: 'Paid', voucher: 'Voucher', payWith: 'Pay securely with', knet: 'KNET', card: 'Credit / Debit card',
    chatWithUs: 'Chat with us', readyTitle: 'Ready to Start Your Journey?', readySub: 'Contact us today and let us plan your perfect vacation', getInTouch: 'Get in Touch',
    searchFlights: 'Search Flights', findHotels: 'Find Hotels', quickLinks: 'Quick Links', ourServicesFooter: 'Our Services', contactUs: 'Contact Us',
    footerTagline: 'Your trusted partner for unforgettable travel experiences across India and the world.',
    rights: 'All rights reserved.',
  },
  ar: {
    home: 'الرئيسية', destinations: 'الوجهات', packages: 'الباقات', flights: 'الطيران', hotels: 'الفنادق', contact: 'اتصل بنا',
    login: 'تسجيل الدخول', logout: 'تسجيل الخروج', myBookings: 'حجوزاتي', myProfile: 'ملفي الشخصي', adminPanel: 'لوحة الإدارة',
    heroTitle: 'اكتشف مغامرتك القادمة', heroSub: 'من جبال الهيمالايا إلى المالديف — رحلات مصممة بعناية من تريب سول للسياحة، الكويت',
    exploreDestinations: 'استكشف الوجهات', viewPackages: 'عرض الباقات',
    popularDestinations: 'الوجهات الأكثر شعبية', popularSub: 'استكشف وجهاتنا المحبوبة في الهند وحول العالم',
    featuredPackages: 'الباقات المميزة', featuredSub: 'تجارب سفر مختارة بعناية لذكريات لا تُنسى',
    ourServices: 'خدماتنا', servicesSub: 'كل ما تحتاجه لرحلة مثالية',
    testimonials: 'ماذا يقول مسافرونا', testimonialsSub: 'تجارب حقيقية من عملائنا الراضين',
    writeReview: 'اكتب تقييماً', loginToReview: 'سجّل الدخول لكتابة تقييم',
    bookNow: 'احجز الآن', viewMap: 'عرض الخريطة', photo: 'الصورة', viewItinerary: 'عرض البرنامج', bookPackage: 'احجز هذه الباقة',
    perPerson: 'للشخص', search: 'بحث', reserve: 'احجز', book: 'احجز',
    payNow: 'ادفع الآن', paid: 'مدفوع', voucher: 'قسيمة الحجز', payWith: 'ادفع بأمان عبر', knet: 'كي نت', card: 'بطاقة ائتمان / خصم',
    chatWithUs: 'تحدث معنا', readyTitle: 'هل أنت مستعد لبدء رحلتك؟', readySub: 'تواصل معنا اليوم ودعنا نخطط لعطلتك المثالية', getInTouch: 'تواصل معنا',
    searchFlights: 'ابحث عن رحلات طيران', findHotels: 'ابحث عن فنادق', quickLinks: 'روابط سريعة', ourServicesFooter: 'خدماتنا', contactUs: 'اتصل بنا',
    footerTagline: 'شريكك الموثوق لتجارب سفر لا تُنسى في الهند وحول العالم.',
    rights: 'جميع الحقوق محفوظة.',
  },
};

const LanguageContext = createContext(null);

export const LanguageProvider = ({ children }) => {
  const [lang, setLangState] = useState(() => localStorage.getItem('lang') || 'en');

  useEffect(() => {
    document.documentElement.lang = lang;
    document.documentElement.dir = lang === 'ar' ? 'rtl' : 'ltr';
  }, [lang]);

  const setLang = useCallback((l) => { localStorage.setItem('lang', l); setLangState(l); }, []);
  const t = useCallback((key) => dict[lang][key] ?? dict.en[key] ?? key, [lang]);
  const loc = useCallback((obj, field) => (lang === 'ar' && obj?.[`${field}_ar`]) || obj?.[field] || '', [lang]);

  return <LanguageContext.Provider value={{ lang, setLang, t, loc, isRtl: lang === 'ar' }}>{children}</LanguageContext.Provider>;
};

export const useLang = () => useContext(LanguageContext);
